import AuthApiUtils from './auth';

/**
 * Service for managing JWT token refresh
 * Handles automatic token refresh before expiration
 */
class TokenRefreshService {
  private refreshPromise: Promise<string | null> | null = null;
  private refreshBuffer = 60 * 1000; // 1 minute buffer before expiration

  /**
   * Checks if the access token needs refresh and refreshes it if necessary
   * @returns The current (or newly refreshed) access token, or null if refresh fails
   */
  async refreshTokenIfNeeded(): Promise<string | null> {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const accessToken = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      return accessToken;
    }

    // Check if access token is expired or about to expire
    if (this.isTokenExpiringSoon(accessToken)) {
      return this.refreshToken(refreshToken);
    }

    return accessToken;
  }

  /**
   * Decodes a base64url string to a regular string
   * JWT uses base64url encoding (RFC 7519) which differs from standard base64:
   * - Uses '-' instead of '+'
   * - Uses '_' instead of '/'
   * - Padding '=' may be omitted
   */
  private decodeBase64Url(base64url: string): string {
    // Replace base64url characters with standard base64 characters
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if necessary
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    return atob(base64);
  }

  /**
   * Checks if the token is expired or will expire within the buffer period
   */
  private isTokenExpiringSoon(token: string | null): boolean {
    if (!token) return true;

    try {
      // JWT structure: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      const payload = JSON.parse(this.decodeBase64Url(parts[1]));

      // Check if token has expiration claim
      if (!payload.exp) {
        // Legacy token without expiration - don't refresh
        return false;
      }

      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();

      return now >= expiresAt - this.refreshBuffer;
    } catch {
      // If we can't decode the token, assume it needs refresh
      return true;
    }
  }

  /**
   * Refreshes the access token using the refresh token
   * Prevents multiple simultaneous refresh requests
   */
  private async refreshToken(refreshToken: string): Promise<string | null> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = AuthApiUtils.refreshToken(refreshToken)
      .then(
        (data: {
          accessToken: string;
          refreshToken?: string;
          expiresIn: number;
        }) => {
          // Update localStorage with new access token
          localStorage.setItem('authToken', data.accessToken);

          // Token rotation: store new refresh token if provided
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }

          // Update expiration timestamp
          const expiresAt = Date.now() + data.expiresIn * 1000;
          localStorage.setItem('tokenExpiresAt', expiresAt.toString());

          this.refreshPromise = null;
          return data.accessToken;
        },
      )
      .catch((error: Error) => {
        console.error('Token refresh failed:', error);

        // Refresh token invalid - dispatch session expired event
        this.clearTokens();
        this.refreshPromise = null;

        // Dispatch event so the app can handle session expiry
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }

        return null;
      });

    return this.refreshPromise;
  }

  /**
   * Clears all auth tokens from localStorage
   */
  private clearTokens(): void {
    if (typeof localStorage === 'undefined') return;

    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('profile');
    localStorage.removeItem('tokenExpiresAt');
  }

  /**
   * Checks if the user has a valid session (has refresh token)
   */
  hasValidSession(): boolean {
    if (typeof localStorage === 'undefined') return false;
    return !!localStorage.getItem('refreshToken');
  }

  /**
   * Gets the token expiration time in milliseconds
   */
  getTokenExpiresAt(): number | null {
    if (typeof localStorage === 'undefined') return null;
    const expiresAt = localStorage.getItem('tokenExpiresAt');
    return expiresAt ? parseInt(expiresAt, 10) : null;
  }
}

export const tokenRefreshService = new TokenRefreshService();
